import type { CompetitionDetail } from '@/api/competitionDetail'
import { Badge, formatInteger } from '@/design'

import { formatMinor } from '../list/cells'
import { SellThrough, statusLabel } from '../competitions/columns'
import { formatInstant } from './format'
import { UNDEFINED_FIGURE } from './reasons'

/**
 * Who the competition is: its status, its dates (in the tenant's timezone), its ticket
 * price (in its own currency, not converted) and how much of it has sold, lifetime, as
 * the website reports it. Every figure is the summary's, the list's row.
 */
export function DetailHeader({ detail, locale }: { detail: CompetitionDetail; locale: string | undefined }) {
  const { summary: row, timezone } = detail
  const instant = (value: string | null) => (value ? formatInstant(value, timezone, locale) : UNDEFINED_FIGURE)
  const sold =
    row.ticketsSoldLifetime === null
      ? UNDEFINED_FIGURE
      : row.totalTicketCapacity
        ? `${formatInteger(row.ticketsSoldLifetime)} of ${formatInteger(row.totalTicketCapacity)}`
        : formatInteger(row.ticketsSoldLifetime)

  return (
    <section className="cd-header" aria-label="Competition details">
      <div className="cd-header__badges">
        <Badge tone={row.active ? 'positive' : 'neutral'}>{statusLabel(row.status)}</Badge>
        {row.category ? <span className="cd-header__category">{row.category}</span> : null}
        {row.productUrl ? (
          <a className="cd-header__link" href={row.productUrl} target="_blank" rel="noopener noreferrer">
            View on website<span className="ci-visually-hidden"> (opens in a new tab)</span>
          </a>
        ) : null}
      </div>
      <dl className="cd-facts">
        <div>
          <dt>Starts</dt>
          <dd>{instant(row.startAt)}</dd>
        </div>
        <div>
          <dt>Ends</dt>
          <dd>{instant(row.endAt)}</dd>
        </div>
        <div>
          <dt>Draw</dt>
          <dd>{instant(row.drawAt)}</dd>
        </div>
        <div>
          <dt>Ticket price</dt>
          <dd>{row.ticketPriceMinor === null ? UNDEFINED_FIGURE : formatMinor(row.ticketPriceMinor, row.ticketPriceCurrency, locale)}</dd>
        </div>
        <div>
          <dt>Sold to date</dt>
          <dd>
            {sold}
            {row.sellThrough === null ? null : (
              <>
                {' '}
                <SellThrough value={row.sellThrough} />
              </>
            )}
          </dd>
        </div>
      </dl>
    </section>
  )
}
